import { useState, useEffect, useRef, useMemo } from "react";
import { LuCog, LuRefreshCw } from "react-icons/lu";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useAuth, useUser } from "@clerk/react";
import { JSONTree } from "react-json-tree";
import type {
  RoomConfig,
  RoomSnapshot,
  SourceConfig,
  SourceSnapshot,
} from "@tuit/shared-types";
import { SourcePanel } from "../components/SourcePanel";
import {
  adminListRooms,
  adminListSources,
  adminUpdateRoomConfig,
  adminDeleteRoomSnapshot,
} from "../api";
import { TopBar } from "../components/TopBar";
import adminStyles from "../styles/components/AdminPage.module.css";
import styles from "../styles/pages/AdminRoomEditorPage.module.css";

function WrenchIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8.5 2a2 2 0 0 0-1.8 2.8L2.5 9a1 1 0 1 0 1.5 1.5l4.2-4.2A2 2 0 1 0 8.5 2z" />
      <path d="M8.1 2.8L7 3.9l.6.6 1.1-1.1" />
    </svg>
  );
}

export function AdminRoomEditorPage() {
  const { roomId } = useParams({ strict: false }) as { roomId: string };
  const { isLoaded, userId } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";

  useEffect(() => {
    if (isLoaded && (!userId || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [isLoaded, userId, isAdmin, navigate]);

  useEffect(() => {
    document.title = `TwiceDaily | ${roomId} (edit)`;
    return () => {
      document.title = "TwiceDaily";
    };
  }, [roomId]);

  const [config, setConfig] = useState<RoomConfig | null>(null);
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [allSources, setAllSources] = useState<SourceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  // Form fields
  const [title, setTitle] = useState("");
  const [refreshInterval, setRefreshInterval] = useState(360);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [committed, setCommitted] = useState<{
    title: string;
    refreshInterval: number;
    sourceIds: string[];
  } | null>(null);

  // Source picker
  const [sourceSearch, setSourceSearch] = useState("");

  // Drag-and-drop reorder
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // Refs for scrolling to source panels in the preview pane
  const sourcePanelRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Gear menu / clone dialog
  const [showGearMenu, setShowGearMenu] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);

  // Clone dialog state
  const [cloneRooms, setCloneRooms] = useState<RoomConfig[]>([]);
  const [cloneRoomInput, setCloneRoomInput] = useState("");
  const [cloneSources, setCloneSources] = useState<{ id: string; title: string }[] | null>(null);
  const [cloneChecked, setCloneChecked] = useState<Set<string>>(new Set());
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Preview
  const [previewView, setPreviewView] = useState<"json" | "ux">("ux");

  // Source picker preview overlay
  const [pickerPreview, setPickerPreview] = useState<{
    sourceId: string;
    snapshot: SourceSnapshot | null;
    loading: boolean;
    error: string | null;
  } | null>(null);

  const isDirty =
    committed !== null &&
    (title !== committed.title ||
      refreshInterval !== committed.refreshInterval ||
      JSON.stringify(sourceIds) !== JSON.stringify(committed.sourceIds));

  function applyConfig(c: RoomConfig) {
    setTitle(c.title);
    setRefreshInterval(c.refreshIntervalMinutes);
    setSourceIds([...c.sourceIds]);
    setCommitted({
      title: c.title,
      refreshInterval: c.refreshIntervalMinutes,
      sourceIds: [...c.sourceIds],
    });
    setConfig(c);
  }

  useEffect(() => {
    if (!roomId) return;
    setLoading(true);
    setLoadError(null);
    setSaveSuccess(false);
    setSaveError(null);
    setSnapshot(null);
    setLoadingSnapshot(true);

    // Config + sources — needed for the form
    Promise.all([
      fetch(`/api/room/${roomId}/config`).then((r) => {
        if (!r.ok) throw new Error(`Config not found (HTTP ${r.status})`);
        return r.json() as Promise<RoomConfig>;
      }),
      adminListSources(),
    ])
      .then(([cfg, sources]) => {
        applyConfig(cfg);
        setAllSources(sources);
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));

    // Snapshot — parallel, non-blocking; populates status pips + preview
    fetch(`/api/room/${roomId}/snapshot`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<RoomSnapshot>;
      })
      .then((snap) => setSnapshot(snap))
      .catch(() => {})
      .finally(() => setLoadingSnapshot(false));
  }, [roomId]);

  // Fetch all rooms when clone dialog opens; reset state on close
  useEffect(() => {
    if (!showCloneDialog) return;
    setCloneRoomInput("");
    setCloneSources(null);
    setCloneChecked(new Set());
    setCloneError(null);
    adminListRooms().then(setCloneRooms).catch(() => {});
  }, [showCloneDialog]);

  // Fetch sources for the selected room whenever the input matches a known room ID
  useEffect(() => {
    if (!showCloneDialog) return;
    const matched = cloneRooms.find((r) => r.id === cloneRoomInput);
    if (!matched) {
      setCloneSources(null);
      setCloneChecked(new Set());
      return;
    }
    setCloneLoading(true);
    setCloneError(null);
    setCloneSources(null);
    fetch(`/api/room/${matched.id}/config`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<RoomConfig>;
      })
      .then((cfg) => {
        const newIds = cfg.sourceIds.filter((id) => !sourceIds.includes(id));
        const sources = newIds.map((id) => ({
          id,
          title: allSources.find((s) => s.id === id)?.title ?? id,
        }));
        setCloneSources(sources);
        setCloneChecked(new Set(newIds));
      })
      .catch((err: unknown) => setCloneError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setCloneLoading(false));
  // sourceIds and allSources are intentionally omitted — snapshot at open time is correct
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneRoomInput, cloneRooms, showCloneDialog]);

  function handleClone() {
    const toAdd = [...cloneChecked].filter((id) => !sourceIds.includes(id));
    setSourceIds((prev) => [...prev, ...toAdd]);
    setShowCloneDialog(false);
  }

  async function loadSnapshot() {
    setLoadingSnapshot(true);
    try {
      const res = await fetch(`/api/room/${roomId}/snapshot`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSnapshot((await res.json()) as RoomSnapshot);
    } finally {
      setLoadingSnapshot(false);
    }
  }

  async function handleRebuild() {
    setRebuilding(true);
    setSnapshot(null);
    try {
      await adminDeleteRoomSnapshot(roomId);
      const res = await fetch(`/api/room/${roomId}/snapshot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceIds }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSnapshot((await res.json()) as RoomSnapshot);
    } finally {
      setRebuilding(false);
    }
  }

  function handleDragStart(i: number) {
    dragIndex.current = i;
  }

  function handleDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (dragIndex.current !== null && dragIndex.current !== i) {
      setDragOver(i);
    }
  }

  function handleDrop(e: React.DragEvent, i: number) {
    e.preventDefault();
    const from = dragIndex.current;
    setDragOver(null);
    dragIndex.current = null;
    if (from === null || from === i) return;
    setSourceIds((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(i, 0, item!);
      return next;
    });
  }

  function handleDragEnd() {
    dragIndex.current = null;
    setDragOver(null);
  }

  async function handlePickerPreview(sourceId: string) {
    setPickerPreview({ sourceId, snapshot: null, loading: true, error: null });
    try {
      const res = await fetch(`/api/source/${sourceId}/snapshot`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPickerPreview({
        sourceId,
        snapshot: (await res.json()) as SourceSnapshot,
        loading: false,
        error: null,
      });
    } catch (err: unknown) {
      setPickerPreview({
        sourceId,
        snapshot: null,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load",
      });
    }
  }

  function closePickerPreview() {
    setPickerPreview(null);
  }

  function scrollToSource(id: string) {
    if (!snapshot) return;
    // Switch to UX view first if needed, then scroll after re-render
    if (previewView !== "ux") setPreviewView("ux");
    requestAnimationFrame(() => {
      sourcePanelRefs.current
        .get(id)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }

  function removeSource(id: string) {
    setSourceIds((prev) => prev.filter((s) => s !== id));
  }

  function addSource(id: string) {
    if (!sourceIds.includes(id)) {
      setSourceIds((prev) => [...prev, id]);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      const updated = await adminUpdateRoomConfig(roomId, {
        title,
        refreshIntervalMinutes: refreshInterval,
        sourceIds,
      });
      applyConfig(updated);
      setSaveSuccess(true);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
      setSaving(false);
      return;
    }
    setSaving(false);
    await handleRebuild();
  }

  // sourceId -> fetchStatus from the latest snapshot
  const sourceStatusMap = useMemo(
    () =>
      new Map(
        snapshot?.sourceSnapshots.map((s) => [s.sourceId, s.fetchStatus]) ?? [],
      ),
    [snapshot],
  );

  // Map sourceId -> SourceConfig for display
  const sourceMap = new Map(allSources.map((s) => [s.id, s]));

  // Sources available to add: not already in room, matching search
  const availableSources = allSources.filter(
    (s) =>
      !sourceIds.includes(s.id) &&
      (!sourceSearch ||
        s.title.toLowerCase().includes(sourceSearch.toLowerCase()) ||
        s.id.toLowerCase().includes(sourceSearch.toLowerCase())),
  );

  if (!isLoaded || !userId || !isAdmin) return null;

  return (
    <div className={styles.page}>
      <TopBar />
      <div className={styles.content}>
        <nav className={styles.topbar}>
          <Link to="/admin/rooms" className={styles.back}>
            ← Rooms
          </Link>
          <span className={styles.sep}>›</span>
          <h1 className={styles.title}>{config?.title ?? roomId}</h1>
        </nav>

        <div className={styles.layout}>
          {/* LEFT PANE — form */}
          <div className={styles.formPane}>
            <div className={adminStyles.panel}>
              <div className={adminStyles.panelHeader}>
                <h2 className={adminStyles.panelTitle}>Edit Room</h2>
              </div>
              <div className={adminStyles.panelBody}>
                {loading ? (
                  <span className={adminStyles.panelEmpty}>Loading…</span>
                ) : loadError ? (
                  <div className={adminStyles.opsResultError}>{loadError}</div>
                ) : (
                  <form onSubmit={handleSave} className={adminStyles.editForm}>
                    <div className={adminStyles.editRow}>
                      <span className={adminStyles.editLabel}>ID</span>
                      <span className={styles.idValue}>{roomId}</span>
                    </div>
                    <div className={adminStyles.editRow}>
                      <label
                        htmlFor="re-title"
                        className={adminStyles.editLabel}
                      >
                        Title
                      </label>
                      <input
                        id="re-title"
                        className={adminStyles.editInput}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div className={adminStyles.editRow}>
                      <label
                        htmlFor="re-refresh"
                        className={adminStyles.editLabel}
                      >
                        Refresh (min)
                      </label>
                      <input
                        id="re-refresh"
                        className={adminStyles.editInput}
                        type="number"
                        min={1}
                        value={refreshInterval}
                        onChange={(e) =>
                          setRefreshInterval(Number(e.target.value))
                        }
                        required
                      />
                    </div>

                    {/* Current sources */}
                    <div className={styles.sourcesSection}>
                      <div className={styles.sourcesSectionHeader}>
                        <span className={styles.sourcesSectionTitle}>
                          Sources ({sourceIds.length})
                        </span>
                        <div className={styles.gearMenuAnchor}>
                          <button
                            type="button"
                            className={styles.gearBtn}
                            title="Source options"
                            onClick={() => setShowGearMenu((v) => !v)}
                          >
                            <LuCog size={15} />
                          </button>
                          {showGearMenu && (
                            <>
                              <div
                                className={styles.gearMenuBackdrop}
                                onClick={() => setShowGearMenu(false)}
                              />
                              <div className={styles.gearMenu}>
                                <button
                                  type="button"
                                  className={styles.gearMenuItem}
                                  onClick={() => {
                                    setShowGearMenu(false);
                                    setShowCloneDialog(true);
                                  }}
                                >
                                  Clone sources from..
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        <button
                          type="button"
                          className={styles.gearBtn}
                          title="Rebuild snapshot"
                          disabled={rebuilding || loadingSnapshot}
                          onClick={handleRebuild}
                        >
                          <LuRefreshCw size={14} className={rebuilding || loadingSnapshot ? styles.spinning : undefined} />
                        </button>
                      </div>
                      {sourceIds.length === 0 ? (
                        <div className={styles.sourcesEmpty}>
                          No sources — add some below.
                        </div>
                      ) : (
                        <ul className={styles.sourceList}>
                          {sourceIds.map((id, i) => {
                            const src = sourceMap.get(id);
                            return (
                              <li
                                key={id}
                                className={`${styles.sourceItem} ${dragOver === i ? styles.sourceItemDragOver : ""}`}
                                draggable
                                onDragStart={() => handleDragStart(i)}
                                onDragOver={(e) => handleDragOver(e, i)}
                                onDrop={(e) => handleDrop(e, i)}
                                onDragEnd={handleDragEnd}
                                onDragLeave={() => setDragOver(null)}
                              >
                                <span
                                  className={styles.dragHandle}
                                  title="Drag to reorder"
                                >
                                  ⠿
                                </span>
                                <span className={styles.sourcePos}>
                                  {i + 1}
                                </span>
                                {(() => {
                                  const st = sourceStatusMap.get(id);
                                  return (
                                    <span
                                      className={`${styles.pip} ${
                                        st === "ok"
                                          ? styles.pipOk
                                          : st === "error"
                                            ? styles.pipError
                                            : loadingSnapshot
                                              ? styles.pipLoading
                                              : styles.pipUnknown
                                      }`}
                                      title={
                                        st ??
                                        (loadingSnapshot
                                          ? "loading…"
                                          : "no snapshot")
                                      }
                                    />
                                  );
                                })()}
                                <button
                                  type="button"
                                  className={`${styles.sourceName} ${snapshot ? styles.sourceNameClickable : ""}`}
                                  onClick={() => scrollToSource(id)}
                                  disabled={!snapshot}
                                  title={
                                    snapshot
                                      ? "Scroll to this source in the preview"
                                      : undefined
                                  }
                                >
                                  {src?.title ?? id}
                                </button>
                                <div className={styles.sourceActions}>
                                  <a
                                    href={`/admin/feeds/${id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.wrenchBtn}
                                    title="Open source editor"
                                  >
                                    <WrenchIcon />
                                  </a>
                                  <button
                                    type="button"
                                    className={`${styles.sourceBtn} ${styles.sourceBtnRemove}`}
                                    onClick={() => removeSource(id)}
                                    title="Remove"
                                  >
                                    <svg
                                      width="13"
                                      height="13"
                                      viewBox="0 0 13 13"
                                      fill="none"
                                      aria-hidden="true"
                                    >
                                      <circle
                                        cx="6.5"
                                        cy="6.5"
                                        r="5.75"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                      />
                                      <path
                                        d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    {/* Source picker */}
                    <div className={styles.pickerSection}>
                      <div className={styles.sourcesSectionHeader}>
                        <span className={styles.sourcesSectionTitle}>
                          Add Source
                        </span>
                      </div>
                      <input
                        className={`${adminStyles.editInput} ${styles.pickerSearch}`}
                        placeholder="Search sources…"
                        value={sourceSearch}
                        onChange={(e) => setSourceSearch(e.target.value)}
                      />
                      {availableSources.length === 0 ? (
                        <div className={styles.sourcesEmpty}>
                          {sourceSearch
                            ? "No sources match."
                            : "All catalog sources are in this room."}
                        </div>
                      ) : (
                        <ul className={styles.pickerList}>
                          {availableSources.slice(0, 30).map((s) => (
                            <li key={s.id} className={styles.pickerItem}>
                              <span className={styles.pickerName}>
                                {s.title}
                              </span>
                              <div className={styles.pickerItemActions}>
                                <a
                                  href={`/admin/feeds/${s.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.wrenchBtn}
                                  title="Open source editor"
                                >
                                  <WrenchIcon />
                                </a>
                                <button
                                  type="button"
                                  className={styles.pickerPreviewBtn}
                                  onClick={() => handlePickerPreview(s.id)}
                                  title="Preview this source"
                                >
                                  <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 13 13"
                                    fill="none"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M1 6.5C2 4 4 2 6.5 2S11 4 12 6.5C11 9 9 11 6.5 11S2 9 1 6.5Z"
                                      stroke="currentColor"
                                      strokeWidth="1.4"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <circle
                                      cx="6.5"
                                      cy="6.5"
                                      r="1.75"
                                      stroke="currentColor"
                                      strokeWidth="1.4"
                                    />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className={styles.pickerAddBtn}
                                  onClick={() => addSource(s.id)}
                                  title="Add to room"
                                >
                                  <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 13 13"
                                    fill="none"
                                    aria-hidden="true"
                                  >
                                    <circle
                                      cx="6.5"
                                      cy="6.5"
                                      r="5.75"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                    />
                                    <path
                                      d="M6.5 4V9M4 6.5H9"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </li>
                          ))}
                          {availableSources.length > 30 && (
                            <li className={styles.pickerMore}>
                              +{availableSources.length - 30} more — search to
                              narrow
                            </li>
                          )}
                        </ul>
                      )}
                    </div>

                    {saveError && (
                      <div className={adminStyles.opsResultError}>
                        {saveError}
                      </div>
                    )}
                    {saveSuccess && (
                      <div className={styles.saveSuccess}>Saved.</div>
                    )}
                    <div className={adminStyles.editActions}>
                      <button
                        type="submit"
                        className={adminStyles.actionBtn}
                        disabled={saving || !isDirty}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT PANE — preview */}
          <div className={styles.previewPane}>
            <div className={styles.previewPanel}>
              <div className={styles.previewHeader}>
                <div className={styles.previewLabelGroup}>
                  <span className={styles.previewLabel}>Preview</span>
                  {snapshot && (
                    <span className={styles.previewMeta}>
                      {snapshot.sourceSnapshots.length} sources ·{" "}
                      {new Date(snapshot.generatedAt).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className={styles.previewControls}>
                  <div className={adminStyles.viewToggle}>
                    {(["json", "ux"] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={`${adminStyles.viewToggleBtn} ${previewView === v ? adminStyles.viewToggleBtnActive : ""}`}
                        onClick={() => setPreviewView(v)}
                      >
                        {v.toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={adminStyles.actionBtn}
                    onClick={handleRebuild}
                    disabled={rebuilding || loadingSnapshot}
                  >
                    {rebuilding ? "Rebuilding…" : "Rebuild"}
                  </button>
                </div>
              </div>

              {previewView === "json" &&
                (snapshot ? (
                  <div className={styles.jsonPane}>
                    <JSONTree data={snapshot} />
                  </div>
                ) : (
                  <div className={adminStyles.panelBody}>
                    <span className={adminStyles.panelEmpty}>
                      {rebuilding || loadingSnapshot
                        ? "Loading snapshot…"
                        : "No snapshot cached — click Rebuild."}
                    </span>
                  </div>
                ))}

              {previewView === "ux" &&
                (snapshot ? (
                  <div className={styles.uxPane}>
                    {snapshot.sourceSnapshots.map((s) => (
                      <div
                        key={s.sourceId}
                        ref={(el) => {
                          if (el) sourcePanelRefs.current.set(s.sourceId, el);
                          else sourcePanelRefs.current.delete(s.sourceId);
                        }}
                      >
                        <SourcePanel section={s} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={adminStyles.panelBody}>
                    <span className={adminStyles.panelEmpty}>
                      {rebuilding || loadingSnapshot
                        ? "Loading snapshot…"
                        : "No snapshot cached — click Rebuild."}
                    </span>
                  </div>
                ))}
            </div>

            {pickerPreview && (
              <div className={styles.pickerOverlay}>
                <div className={styles.pickerOverlayHeader}>
                  <span className={styles.pickerOverlayTitle}>
                    {allSources.find((s) => s.id === pickerPreview.sourceId)
                      ?.title ?? pickerPreview.sourceId}
                  </span>
                  <button
                    type="button"
                    className={styles.pickerOverlayClose}
                    onClick={closePickerPreview}
                    title="Close preview"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="7"
                        cy="7"
                        r="6.25"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
                <div className={styles.pickerOverlayBody}>
                  {pickerPreview.loading ? (
                    <span className={adminStyles.panelEmpty}>Loading…</span>
                  ) : pickerPreview.error ? (
                    <span className={adminStyles.opsResultError}>
                      {pickerPreview.error}
                    </span>
                  ) : pickerPreview.snapshot ? (
                    <SourcePanel section={pickerPreview.snapshot} />
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCloneDialog && (
        <div
          className={styles.dialogBackdrop}
          onClick={() => setShowCloneDialog(false)}
        >
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <span className={styles.dialogTitle}>Clone Sources From..</span>
              <button
                type="button"
                className={styles.dialogClose}
                onClick={() => setShowCloneDialog(false)}
                aria-label="Close"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 3L11 11M11 3L3 11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className={styles.dialogBody}>
              <div className={styles.cloneRow}>
                <label htmlFor="clone-room-input" className={styles.cloneLabel}>Room ID</label>
                <input
                  id="clone-room-input"
                  list="clone-rooms-datalist"
                  className={styles.cloneInput}
                  value={cloneRoomInput}
                  onChange={(e) => setCloneRoomInput(e.target.value)}
                  placeholder="Type or select a room…"
                  autoComplete="off"
                  autoFocus
                />
                <datalist id="clone-rooms-datalist">
                  {cloneRooms.filter((r) => r.id !== roomId).map((r) => (
                    <option key={r.id} value={r.id}>{r.title}</option>
                  ))}
                </datalist>
              </div>

              {cloneLoading && <div className={styles.cloneMsg}>Loading…</div>}
              {cloneError && <div className={styles.cloneMsgError}>{cloneError}</div>}

              {cloneSources !== null && (
                cloneSources.length === 0 ? (
                  <div className={styles.cloneMsg}>No new sources to clone — all already in this room.</div>
                ) : (
                  <>
                    <ul className={styles.cloneList}>
                      {cloneSources.map((s) => (
                        <li key={s.id} className={styles.cloneItem}>
                          <label className={styles.cloneItemLabel}>
                            <input
                              type="checkbox"
                              checked={cloneChecked.has(s.id)}
                              onChange={(e) => {
                                setCloneChecked((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(s.id);
                                  else next.delete(s.id);
                                  return next;
                                });
                              }}
                            />
                            <span className={styles.cloneItemTitle}>{s.title}</span>
                            <span className={styles.cloneItemId}>{s.id}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                    <div className={styles.cloneActions}>
                      <button
                        type="button"
                        className={adminStyles.actionBtn}
                        disabled={cloneChecked.size === 0}
                        onClick={handleClone}
                      >
                        Clone {cloneChecked.size} selected source{cloneChecked.size !== 1 ? "s" : ""}…
                      </button>
                    </div>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
